import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function DemoVideoPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTrialClick = () => {
    // Open trial link in new tab
    window.open('https://fas.st/t/Ucbr3aNe', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </a>
            <button onClick={() => navigate('/')} className="inline-flex items-center cursor-pointer gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-24 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-emerald-100 border border-emerald-200 mb-6">
              <i className="ri-play-circle-line text-emerald-600"></i>
              <span className="text-sm text-emerald-900 font-medium">Product Demo</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
              See InstaVisible in Action
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Watch how our AI-powered platform transforms tax filing, automates compliance, and helps you discover hidden savings across all 50 states.
            </p>
          </div>

          {/* Video Container */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-12">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src="https://www.youtube.com/embed/XRjyZ1n1P8A"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="InstaVisible Demo Video"
              ></iframe>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Tax Practice?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Start your 7-day free trial today and experience the power of AI-driven tax automation
              </p>
              <button 
                onClick={handleTrialClick}
                className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-white text-emerald-700 hover:bg-gray-50 text-lg font-bold px-10 h-16 rounded-xl shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Get 7 Day Trial Now
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-robot-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">45+ AI Experts</h3>
              <p className="text-gray-600">
                Specialized AI agents trained on specific industries to find additional savings
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">30+ Tax Forms</h3>
              <p className="text-gray-600">
                Automated filing for federal and state returns across all entity types
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">50 States Covered</h3>
              <p className="text-gray-600">
                Multi-state tax projections and compliance in one unified platform
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  alt="InstaVisible Tax & Savings Solutions Logo" 
                  className="h-14 w-auto object-contain brightness-0 invert" 
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
                />
              </div>
              <p className="text-white/90 leading-relaxed max-w-md">
                Professional AI-powered tax solutions for CPAs, tax professionals and CFOs. Streamline your workflow with cutting-edge technology.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Services</a></li>
                <li><a href="/ai-agents" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">AI Agents</a></li>
                <li><a href="/blog" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Contact</a></li>
                <li><a href="/privacy-policy" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a></li>
                <li><a href="/terms-of-use" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
